import React, {useEffect, useMemo, useState} from 'react';
import {
    Box,
    Button,
    Checkbox,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    LinearProgress,
    TextField,
    Typography,
} from '@mui/material';
import ReactCrop, {Crop} from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {AxiosResponse} from 'axios';
import {toast} from 'react-toastify';
import {useTranslation} from 'react-i18next';
import {useApiService, useStatsLogger} from '../../../context/ApiServiceContext';
import {PresignedUploadResponse, uploadFileToS3} from '../../../utils/uploadUtils';
import {pollBgRemovalUntilComplete} from '../../../services/jobService';
import Asset from '../../../models/Asset';
import {AiPromptVariant} from '../../../models/Template';
import {StatEventType} from '../../../models/StatEvent';
import {useTemplate} from '../../../context/TemplateContext';
import {computeCenteredCrop, cropImageToBlob, cropOutputSize, MAX_UPLOAD_HEIGHT, PercentCrop} from './cropUtils';
import AiMagicStage from '../AiMagic/AiMagicStage';

const MAX_VIDEO_SIZE_MB = 300;
const MAX_VIDEO_DURATION_SEC = 180;

interface QuickUploadDialogProps {
    open: boolean;
    file: File | null;
    folderId: string;
    /** The image frame on the board. NOT a crop lock any more — a replaced image
     *  is contained inside the frame at render time, so the quick upload is
     *  free-form. Still the target aspect the AI stage generates at. */
    frameSize?: { width: number; height: number };
    /** The input this upload targets — needed to start an AI edit job. */
    inputKey: string;
    designKey?: string;
    /** This field offers AI editing AND the team may use it. When true the
     *  dialog stays open after the upload and shows the AI magic stage. */
    aiEditable?: boolean;
    /** Named AI style options (names only) for the AI magic picker. */
    aiPromptVariants?: AiPromptVariant[];
    /** Field removes background → the AI stage skips its result-crop step. */
    aiRemoveBackground?: boolean;
    onClose: () => void;
    /** Fires with the created asset — the caller applies it to the canvas
     *  immediately (never leave an upload unapplied). Applying does NOT close
     *  the dialog; the dialog owns its own dismissal. */
    onApply: (asset: Asset) => void;
}

/** Fill Mode's one-decision upload: crop (locked to the frame) → אישור.
 *  Remove-background and file naming hide behind "אפשרויות נוספות". */
const QuickUploadDialog: React.FC<QuickUploadDialogProps> = ({
                                                                 open,
                                                                 file,
                                                                 folderId,
                                                                 frameSize,
                                                                 inputKey,
                                                                 designKey,
                                                                 aiEditable,
                                                                 aiPromptVariants,
                                                                 aiRemoveBackground,
                                                                 onClose,
                                                                 onApply,
                                                             }) => {
    const {t} = useTranslation();
    const apiService = useApiService();
    const statsLogger = useStatsLogger();
    const {template} = useTemplate();

    const [previewSrc, setPreviewSrc] = useState('');
    const [imageDims, setImageDims] = useState<{ width: number; height: number } | null>(null);
    const [crop, setCrop] = useState<Crop>({unit: '%', x: 0, y: 0, width: 100, height: 100});
    const [percentCrop, setPercentCrop] = useState<PercentCrop>({unit: '%', x: 0, y: 0, width: 100, height: 100});
    const [advancedOpen, setAdvancedOpen] = useState(false);
    const [removeBackground, setRemoveBackground] = useState(false);
    const [fileName, setFileName] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [bgRemovalActive, setBgRemovalActive] = useState(false);
    const [validationError, setValidationError] = useState<string | null>(null);
    const [videoMeta, setVideoMeta] = useState<{ width: number; height: number; duration: number } | null>(null);
    /** Set once the plain upload is applied and this field offers AI editing —
     *  the dialog then swaps its body to the AI magic stage instead of closing. */
    const [aiSource, setAiSource] = useState<Asset | null>(null);

    const isVideo = !!file?.type.startsWith('video/');

    // Load the picked file: preview + natural size + default centered crop.
    useEffect(() => {
        if (!file || !open) return;
        setFileName(file.name.replace(/\.[^/.]+$/, ''));
        setRemoveBackground(false);
        setAdvancedOpen(false);
        setUploadProgress(0);
        setValidationError(null);
        setVideoMeta(null);
        setIsLoading(false);
        setBgRemovalActive(false);
        setAiSource(null);

        // Guard the async metadata callbacks: after cleanup (file replaced /
        // dialog unmounted) they must not write state or read a revoked URL.
        let cancelled = false;
        const objectUrl = URL.createObjectURL(file);
        if (isVideo) {
            if (file.size > MAX_VIDEO_SIZE_MB * 1024 * 1024) {
                setValidationError(t('editor.uploadImageDialog.validation.videoTooLarge', {
                    size: (file.size / (1024 * 1024)).toFixed(2), max: MAX_VIDEO_SIZE_MB,
                }));
            }
            const video = document.createElement('video');
            video.preload = 'metadata';
            video.onloadedmetadata = () => {
                if (cancelled) return;
                if (video.duration > MAX_VIDEO_DURATION_SEC) {
                    setValidationError(t('editor.uploadImageDialog.validation.videoTooLong', {
                        duration: video.duration.toFixed(1), max: MAX_VIDEO_DURATION_SEC,
                    }));
                }
                setVideoMeta({width: video.videoWidth, height: video.videoHeight, duration: video.duration});
            };
            video.onerror = () => {
                if (!cancelled) setValidationError(t('editor.uploadImageDialog.validation.videoMetadataFailed'));
            };
            video.src = objectUrl;
            setPreviewSrc(objectUrl);
        } else {
            const img = new Image();
            img.onload = () => {
                if (cancelled) return;
                setImageDims({width: img.width, height: img.height});
                // FREE-FORM: no frame aspect, so this is the whole image. A
                // replaced image is contained inside the field box at render
                // time, so pre-cropping it to the frame would discard pixels
                // the render would have shown. Cropping stays available, it
                // just no longer starts anywhere but the full frame.
                const initial = computeCenteredCrop(img.width, img.height);
                setCrop(initial);
                setPercentCrop(initial);
            };
            img.src = objectUrl;
            setPreviewSrc(objectUrl);
        }
        return () => {
            cancelled = true;
            URL.revokeObjectURL(objectUrl);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [file, open]);

    const outputSize = useMemo(() => {
        if (isVideo && videoMeta) return {width: videoMeta.width, height: videoMeta.height};
        if (!imageDims) return {width: 0, height: 0};
        return cropOutputSize(imageDims.width, imageDims.height, percentCrop, MAX_UPLOAD_HEIGHT);
    }, [isVideo, videoMeta, imageDims, percentCrop]);

    const logEvent = (eventType: StatEventType.ASSET_UPLOADED | StatEventType.ASSET_UPLOAD_START | StatEventType.ASSET_UPLOAD_FAILED) => {
        if (template) {
            statsLogger.logEvent({eventType, templateId: template.id, templateName: template.name});
        }
    };

    const failUpload = (error: unknown) => {
        console.error('Fill Mode upload failed', error);
        logEvent(StatEventType.ASSET_UPLOAD_FAILED);
        toast.error(t('editor.upload.failed'));
        setIsLoading(false);
        setBgRemovalActive(false);
    };

    const handleConfirm = async () => {
        if (!file || !folderId || validationError) return;
        // Videos must have their metadata (dimensions/duration) before upload.
        if (isVideo && !videoMeta) return;
        setIsLoading(true);
        try {
            let uploadFile: File;
            let mimeType: string;
            let uploadDims = outputSize;
            if (isVideo) {
                uploadFile = file;
                mimeType = file.type;
            } else {
                const img = new Image();
                const loaded = new Promise<void>((resolve, reject) => {
                    img.onload = () => resolve();
                    img.onerror = () => reject(new Error('Image load failed'));
                });
                img.src = previewSrc;
                await loaded;
                // AI-editable fields feed this image to the model as the reference,
                // so it must be the WHOLE subject — any crop here would hand the
                // AI a head/legs-chopped fragment. Upload the full image (still
                // downscaled); the AI generates at the frame's aspect. Non-AI
                // fields upload whatever the (free-form) crop selected.
                const cropForUpload = (aiEditable && !isVideo)
                    ? {unit: '%', x: 0, y: 0, width: 100, height: 100} as PercentCrop
                    : percentCrop;
                const blob = await cropImageToBlob(img, cropForUpload, file.type || 'image/png', MAX_UPLOAD_HEIGHT);
                mimeType = file.type || 'image/png';
                uploadFile = new File([blob], fileName, {type: mimeType});
                uploadDims = cropOutputSize(img.naturalWidth, img.naturalHeight, cropForUpload, MAX_UPLOAD_HEIGHT);
            }

            logEvent(StatEventType.ASSET_UPLOAD_START);
            const presignedRes: AxiosResponse<PresignedUploadResponse> = await apiService.api.post(
                `/folder/${folderId}/asset/upload`, {}, {headers: {'Content-Type': mimeType}}
            );
            const {presignedPost, url: assetUrl} = presignedRes.data;

            await new Promise<void>((resolve, reject) => {
                uploadFileToS3(presignedPost, uploadFile, setUploadProgress, () => resolve(), (err) => reject(new Error(err)));
            });

            const payload: Record<string, unknown> = {
                name: fileName,
                url: assetUrl,
                type: file.type,
                width: uploadDims.width,
                height: uploadDims.height,
            };
            if (isVideo && videoMeta) payload.duration = videoMeta.duration;
            if (!isVideo && removeBackground) payload.removeBackground = true;

            const response = await apiService.api.put<Asset | { jobId: string }>(`/folder/${folderId}/asset/upload`, payload);

            let asset: Asset;
            if (removeBackground && 'jobId' in response.data) {
                setBgRemovalActive(true);
                const result = await pollBgRemovalUntilComplete(apiService.api, response.data.jobId, () => {});
                if (!result.asset) throw new Error('Background removal returned no asset');
                asset = result.asset;
            } else {
                asset = response.data as Asset;
            }

            logEvent(StatEventType.ASSET_UPLOADED);
            setIsLoading(false);
            setBgRemovalActive(false);
            // The plain photo is applied FIRST, always — the AI step is a bonus
            // on top of a working image, never a prerequisite for one.
            onApply(asset);
            if (aiEditable && !isVideo) {
                setAiSource(asset);
                return;
            }
            onClose();
        } catch (error) {
            failUpload(error);
        }
    };

    // Stage 2 — the plain image is already on the board; offer the AI edit.
    if (aiSource) {
        return (
            <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
                <DialogTitle sx={{pb: 1}}>{t('aiEdit.title')}</DialogTitle>
                <DialogContent sx={{pb: 3}}>
                    <AiMagicStage
                        api={apiService.api}
                        templateId={template?.id ?? ''}
                        inputKey={inputKey}
                        designKey={designKey}
                        sourceAsset={aiSource}
                        aiPromptVariants={aiPromptVariants}
                        frameSize={frameSize}
                        folderId={folderId}
                        removeBackground={aiRemoveBackground}
                        onAccept={(asset) => {
                            onApply(asset);
                            onClose();
                        }}
                        onKeepOriginal={onClose}
                    />
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <Dialog open={open} onClose={isLoading ? undefined : onClose} fullWidth maxWidth="sm">
            <DialogTitle sx={{pb: 1}}>
                {t('editor.fillMode.cropTitle')}
                <Typography sx={{fontSize: '0.85rem', color: '#666'}}>
                    {isVideo || aiEditable ? '' : t('editor.fillMode.cropHint')}
                </Typography>
            </DialogTitle>
            <DialogContent>
                {isVideo ? (
                    <video src={previewSrc} controls style={{width: '100%', maxHeight: '55vh'}}/>
                ) : aiEditable ? (
                    // AI-editable fields upload the WHOLE image (the model needs the
                    // full subject) and framing happens AFTER AI, in AiMagicStage.
                    // So don't show a pre-crop control here — it would be ignored,
                    // silently discarding the user's crop if they keep the original.
                    <img src={previewSrc} alt="preview" style={{width: '100%', maxHeight: '55vh', objectFit: 'contain'}}/>
                ) : (
                    <ReactCrop
                        crop={crop}
                        disabled={isLoading}
                        keepSelection
                        onChange={(newCrop: Crop) => setCrop(newCrop)}
                        onComplete={(_, percentageCrop) => setPercentCrop(percentageCrop as PercentCrop)}
                    >
                        <img src={previewSrc} alt="preview" style={{width: '100%', maxHeight: '55vh', objectFit: 'contain'}}/>
                    </ReactCrop>
                )}

                {validationError && (
                    <Box sx={{mt: 1, p: 1.5, background: '#ffebee', borderRadius: 1, color: '#c62828'}}>
                        {validationError}
                    </Box>
                )}

                {!isVideo && (
                    <>
                        <Button
                            variant="text"
                            onClick={() => setAdvancedOpen((prev) => !prev)}
                            disabled={isLoading}
                            endIcon={<ExpandMoreIcon sx={{transform: advancedOpen ? 'rotate(180deg)' : 'none', transition: '0.2s'}}/>}
                            sx={{mt: 1, textTransform: 'none', color: '#025989'}}
                        >
                            {t('editor.fillMode.advancedOptions')}
                        </Button>
                        <Collapse in={advancedOpen}>
                            <FormControlLabel
                                sx={{display: 'block'}}
                                control={
                                    <Checkbox
                                        checked={removeBackground}
                                        disabled={isLoading}
                                        onChange={(e) => setRemoveBackground(e.target.checked)}
                                    />
                                }
                                label={t('editor.uploadImageDialog.removeBackground')}
                            />
                            <TextField
                                fullWidth
                                size="small"
                                label={t('editor.uploadImageDialog.fileNamePlaceholder')}
                                value={fileName}
                                disabled={isLoading}
                                onChange={(e) => setFileName(e.target.value)}
                                sx={{mt: 1}}
                            />
                        </Collapse>
                    </>
                )}

                {isLoading && (
                    <Box sx={{mt: 2}}>
                        <LinearProgress
                            variant={bgRemovalActive || uploadProgress >= 100 ? 'indeterminate' : 'determinate'}
                            value={uploadProgress}
                        />
                        <Typography sx={{fontSize: '0.8rem', color: '#666', mt: 0.5, textAlign: 'center'}}>
                            {bgRemovalActive ? t('editor.uploadImageDialog.bgStages.removing') : t('editor.fillMode.uploading')}
                        </Typography>
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{px: 3, pb: 2}}>
                <Button onClick={onClose} disabled={isLoading}>
                    {t('editor.uploadImageDialog.cancel')}
                </Button>
                <Button
                    data-testid="fill-upload-confirm"
                    variant="contained"
                    onClick={handleConfirm}
                    disabled={isLoading || !!validationError || (isVideo ? !videoMeta : !imageDims)}
                    sx={{backgroundColor: '#00E162', '&:hover': {backgroundColor: '#3D9A4A'}, borderRadius: '20px', px: 4}}
                >
                    {t('editor.fillMode.confirmUpload')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default QuickUploadDialog;
